class BackInStock extends HTMLElement {
    constructor() {
      super();
      this._form = null;
      this._onSubmitHandler = this.onSubmit.bind(this);
      this._recaptchaSiteKey = this.getAttribute('data-recaptcha-site-key');
      this._recaptchaResponse = null;
    }
  
    connectedCallback() {
      this.setupListeners();
      
      // Set up reCAPTCHA callbacks if enabled
      if (this._recaptchaSiteKey && this._recaptchaSiteKey.trim() !== '') {
        // Initialize reCAPTCHA when ready
        this.waitForRecaptcha();
      } else {
        console.log('reCAPTCHA not enabled - site key not found or empty');
      }
    }
  
    waitForRecaptcha() {
      // Check if reCAPTCHA is already loaded
      if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
        grecaptcha.ready(() => {
          this.initRecaptcha();
        });
        return;
      }
      
      // Set up global onload callback if not already set
      if (!window.onRecaptchaLoad) {
        window.onRecaptchaLoad = () => {
          // Initialize all back-in-stock instances that need reCAPTCHA
          const instances = document.querySelectorAll('back-in-stock[data-recaptcha-site-key]');
          instances.forEach(el => {
            const customElement = el;
            if (customElement && typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
              grecaptcha.ready(() => {
                if (customElement.initRecaptcha) {
                  customElement.initRecaptcha();
                }
              });
            }
          });
        };
      }
      
      // Also check periodically in case onload already fired or script loads differently
      const checkRecaptcha = setInterval(() => {
        if (typeof grecaptcha !== 'undefined') {
          if (grecaptcha.ready) {
            clearInterval(checkRecaptcha);
            grecaptcha.ready(() => {
              this.initRecaptcha();
            });
          } else {
            // If grecaptcha exists but ready is not available, try direct render
            clearInterval(checkRecaptcha);
            setTimeout(() => {
              this.initRecaptcha();
            }, 500);
          }
        }
      }, 100);
      
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkRecaptcha);
      }, 10000);
    }
  
    initRecaptcha() {
      // Prevent double initialization
      if (this._recaptchaWidgetId) {
        return;
      }
      
      const recaptchaWidget = this.querySelector('.g-recaptcha');
      if (!recaptchaWidget) {
        console.warn('reCAPTCHA widget container not found');
        return;
      }
      
      if (typeof grecaptcha === 'undefined') {
        console.warn('grecaptcha not defined');
        return;
      }
      
      if (!this._recaptchaSiteKey || this._recaptchaSiteKey.trim() === '') {
        console.warn('reCAPTCHA site key is missing');
        return;
      }
      
      // Generate unique callback names for this instance
      const instanceId = Math.random().toString(36).substr(2, 9);
      this._instanceId = instanceId;
      
      // Set up global callbacks
      window[`onRecaptchaCallback_${instanceId}`] = (response) => {
        this._recaptchaResponse = response;
        const submitButton = this._form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.disabled = false;
        }
      };
      
      window[`onRecaptchaExpired_${instanceId}`] = () => {
        this._recaptchaResponse = null;
        const submitButton = this._form.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.disabled = true;
        }
      };
      
      // Render reCAPTCHA with callbacks
      try {
        const widgetId = grecaptcha.render(recaptchaWidget, {
          'sitekey': this._recaptchaSiteKey,
          'callback': window[`onRecaptchaCallback_${instanceId}`],
          'expired-callback': window[`onRecaptchaExpired_${instanceId}`]
        });
        this._recaptchaWidgetId = widgetId;
        console.log('reCAPTCHA rendered successfully');
      } catch (error) {
        console.error('Error rendering reCAPTCHA:', error);
      }
    }
  
    setupListeners() {
      // Remove previous listeners if any
      if (this._form) {
        this._form.removeEventListener('submit', this._onSubmitHandler);
      }
  
      // Select the form inside the custom element
      this._form = this.querySelector('form');
      if (this._form) {
        this._form.addEventListener('submit', this._onSubmitHandler);
      }
    }
  
    async onSubmit(e) {
      e.preventDefault();
  
      // Check reCAPTCHA if enabled
      if (this._recaptchaSiteKey) {
        if (!this._recaptchaResponse) {
          // Show error message
          const errorEl = document.createElement('div');
          errorEl.className = 'prd-BackInStock_Message prd-BackInStock_Message-error';
          errorEl.innerHTML = 'Please complete the reCAPTCHA verification.';
          
          // Remove any existing messages
          const existingMessage = this.querySelector('.prd-BackInStock_Message');
          if (existingMessage) {
            existingMessage.remove();
          }
          
          this.appendChild(errorEl);
          return;
        }
      }
  
      const submitButton = this._form.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.disabled = true;
      }
  
      try {
        const response = await this.subscribeToBIS();
  
        const messageDir = this.getAttribute('data-klaviyo-flow-branch') === 'lighting-notification'
          ? 'lightingNotification'
          : 'backInStock';
        const messages = window.theme.strings[messageDir] || {};
        const message = messages[response.ok ? 'success' : 'failure'] || 'An error occurred.';
        const cls = `prd-BackInStock_Message-${response.ok ? 'success' : 'error'}`;
  
        const messageEl = document.createElement('div');
        messageEl.className = `prd-BackInStock_Message ${cls}`;
        messageEl.innerHTML = message;
  
        this._form.style.display = 'none';
        this.appendChild(messageEl);
        
        // Reset reCAPTCHA if enabled
        if (this._recaptchaSiteKey && this._recaptchaWidgetId && typeof grecaptcha !== 'undefined') {
          grecaptcha.reset(this._recaptchaWidgetId);
          this._recaptchaResponse = null;
        }
      } catch (error) {
        console.error('Failed to subscribe:', error);
  
        const errorMessage = 'There was a problem submitting your request.';
        const errorEl = document.createElement('div');
        errorEl.className = 'prd-BackInStock_Message prd-BackInStock_Message-error';
        errorEl.innerHTML = errorMessage;
  
        // Remove any existing messages
        const existingMessage = this.querySelector('.prd-BackInStock_Message');
        if (existingMessage) {
          existingMessage.remove();
        }
        
        this.appendChild(errorEl);
      } finally {
        if (submitButton && !this._recaptchaSiteKey) {
          submitButton.disabled = false;
        } else if (submitButton && this._recaptchaSiteKey) {
          // Keep disabled if reCAPTCHA is required and not completed
          submitButton.disabled = !this._recaptchaResponse;
        }
      }
    }
  
    async subscribeToBIS() {
      const inputEl = this._form.querySelector('[data-el="back-in-stock.input"]').value
      const variantEl = this._form.querySelector('[data-el="back-in-stock.sku"]').value

      const payload = {
        "data": {
          "type": "back-in-stock-subscription",
          "attributes": {
            "profile": {
              "data": {
                  "type": "profile",
                  "attributes": {
                      "email": `${inputEl}`,
                  }
              }
            },
            "channels": ["EMAIL"],
          },
          "relationships": {
            "variant": {
              "data": {
                "type": "catalog-variant",
                "id": `$shopify:::$default:::${variantEl}`
              }
            }
          }
        }
      }

      const url = 'https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=JkKgYN';
  
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          revision: '2024-10-15',
          'content-type': 'application/json',
          Authorization: 'Klaviyo-API-Key pk_3ed6a2ecfcd0618d7774d52208bce50217'
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok.');
      }
  
      return response;
    }
  }
  
  customElements.define('back-in-stock', BackInStock);
  